/**
 * Collection Grid Web Component
 * Lightweight component for collection grid functionality
 */

class CollectionGrid extends HTMLElement {
  constructor() {
    super();
  }

  get prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    // Ensure we don't stack event listeners if the element is reconnected
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();

    this.nav = this.querySelector('.collection-grid__nav');
    this.navItems = this.querySelectorAll('.collection-grid__nav-item');
    this.sections = this.querySelectorAll('.collection-grid__section');
    
    if (this.nav) {
      this.init();
    }
  }

  disconnectedCallback() {
    if (this._abortController) this._abortController.abort();
  }

  init() {
    this.setupHeaderMenuHiding();
    this.setupTabHighlighting();
  }

  setupHeaderMenuHiding() {
    // Handle header menu hiding when navigation becomes sticky
    this._onScrollHeader = this._onScrollHeader || this.handleScroll.bind(this);
    window.addEventListener('scroll', this._onScrollHeader, {
      passive: true,
      signal: this._abortController.signal
    });
  }

  setupTabHighlighting() {
    // Handle click to jump to section
    this.navItems.forEach((navItem, index) => {
      navItem.addEventListener(
        'click',
        (event) => {
          // Prevent native hash jump. We'll manage scrolling + URL update ourselves to avoid
          // fighting with the scroll listener (which causes active-state flicker).
          event.preventDefault();

          const targetSection = this.sections[index];
          if (!targetSection) return;

          const requestedBehavior = 'smooth';
          const behavior = this.prefersReducedMotion ? 'auto' : requestedBehavior;

          // Lock active tab to the clicked one during the programmatic scroll so scrollspy
          // doesn't rapidly flip between indices mid-animation.
          const rect = targetSection.getBoundingClientRect();
          const elementTop = window.scrollY + rect.top;
          const scrollMarginTop = parseFloat(window.getComputedStyle(targetSection).scrollMarginTop) || 0;
          const targetY = elementTop - scrollMarginTop;
          this._programmaticScroll = {
            targetIndex: index,
            targetY,
            startTime: performance.now()
          };

          this.setActiveTab(index, { scrollBehavior: behavior });

          // Use scrollIntoView so CSS `scroll-margin-top` is respected.
          targetSection.scrollIntoView({ behavior, block: 'start' });

          // Update the URL hash without triggering another native scroll.
          const href = navItem.getAttribute('href');
          if (href && href.startsWith('#') && window.history?.pushState) {
            window.history.pushState(null, '', href);
          }
        },
        { signal: this._abortController.signal }
      );
    });

    // Handle scroll to highlight correct tab
    this._onScrollHighlight = this._onScrollHighlight || this.handleTabScroll.bind(this);
    window.addEventListener('scroll', this._onScrollHighlight, {
      passive: true,
      signal: this._abortController.signal
    });

    // Set initial state
    this.handleTabScroll();
  }

  handleTabScroll() {
    const scrollPos = window.scrollY;

    // Different thresholds for mobile and desktop
    const isMobile = window.innerWidth <= 749;
    const scrollThreshold = isMobile ? 200 : 350;

    // Handle collection-grid__nav active class based on scroll position
    // (This should not control which nav item is "active"—only the nav container state.)
    if (scrollPos > 0 && scrollPos < scrollThreshold) {
      this.nav.classList.remove('active');
    } else {
      // >= threshold OR at top
      this.nav.classList.add('active');
    }

    // If we're doing a programmatic scroll (from a tab click), keep the clicked tab active
    // until we arrive (or until a safety timeout), to prevent flicker.
    if (this._programmaticScroll) {
      const { targetIndex, targetY, startTime } = this._programmaticScroll;
      this.setActiveTab(targetIndex);

      const elapsed = performance.now() - startTime;
      const closeEnough = Math.abs(window.scrollY - targetY) <= 4;
      if (closeEnough || elapsed > 2000) {
        this._programmaticScroll = null;
      }
      return;
    }

    // Scrollspy: only update the active tab when at least 30% of a section is visible.
    // This prevents "early" activation when a section just starts entering the viewport.
    const minVisibleRatio = 0.4

    let bestIndex = typeof this._activeIndex === 'number' ? this._activeIndex : 0;
    let bestRatio = 0;

    this.sections.forEach((section, index) => {
      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const visiblePx = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
      const ratio = rect.height > 0 ? Math.max(0, visiblePx) / rect.height : 0;

      if (ratio >= minVisibleRatio && ratio > bestRatio) {
        bestRatio = ratio;
        bestIndex = index;
      }
    });

    // If no section meets the threshold and we don't have a previous active index yet,
    // fall back to the old "passed offsetTop" logic for initial load only.
    if (bestRatio === 0 && typeof this._activeIndex !== 'number') {
      let currentIndex = 0;
      this.sections.forEach((section, index) => {
        if (scrollPos >= section.offsetTop) currentIndex = index;
      });
      bestIndex = currentIndex;
    }

    this.setActiveTab(bestIndex);
  }

  setActiveTab(activeIndex, options = {}) {
    // Remove active class from all nav items
    this.navItems.forEach(item => item.classList.remove('active'));

    // Always keep the current section's tab highlighted, regardless of scroll threshold.
    const activeItem = this.navItems[activeIndex];
    if (!activeItem) return;

    activeItem.classList.add('active');

    // If nav is horizontally scrollable (overflow), keep the active tab in view.
    // Only run this when the active tab changes to avoid jitter while scrolling.
    if (this._activeIndex !== activeIndex) {
      this._activeIndex = activeIndex;
      const requestedBehavior = options.scrollBehavior || 'auto';
      const behavior = this.prefersReducedMotion ? 'auto' : requestedBehavior;
      this.scrollActiveNavItemIntoView(activeItem, { behavior });
    }
  }

  scrollActiveNavItemIntoView(activeItem, { behavior = 'auto' } = {}) {
    if (!this.nav || !activeItem) return;

    const containerRect = this.nav.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();

    // If fully visible, don't scroll.
    const isFullyVisible = itemRect.left >= containerRect.left && itemRect.right <= containerRect.right;
    if (isFullyVisible) return;

    // Respect nav container padding by using scroll-margin on the active item.
    // This avoids "eating" the right-side gap at max scrollLeft while still keeping the item in view.
    const styles = window.getComputedStyle(this.nav);
    const paddingLeft = styles.paddingLeft || '0px';
    const paddingRight = styles.paddingRight || '0px';

    if (this._lastScrollMarginItem && this._lastScrollMarginItem !== activeItem) {
      this._lastScrollMarginItem.style.scrollMarginLeft = '';
      this._lastScrollMarginItem.style.scrollMarginRight = '';
    }
    this._lastScrollMarginItem = activeItem;
    activeItem.style.scrollMarginLeft = paddingLeft;
    activeItem.style.scrollMarginRight = paddingRight;

    // Use rAF so layout is settled before scrolling the container.
    cancelAnimationFrame(this._scrollRafId);
    this._scrollRafId = requestAnimationFrame(() => {
      activeItem.scrollIntoView({
        behavior,
        block: 'nearest',
        inline: 'nearest'
      });
    });
  }

  lastScroll = 0; // Store last scroll position

  handleScroll() {
    const currentScroll = window.scrollY;

    if (currentScroll < 35) {
      // Remove all classes if less than 35px scroll
      this.nav.classList.remove('scrolled', 'scroll-up', 'scroll-down');
    } else {
      // Always keep main scrolled class after 35px
      this.nav.classList.add('scrolled');

      // Direction detection
      if (currentScroll < this.lastScroll) {
        // Scrolling UP
        this.nav.classList.add('scroll-up');
        this.nav.classList.remove('scroll-down');
      } else if (currentScroll > this.lastScroll) {
        // Scrolling DOWN
        this.nav.classList.add('scroll-down');
        this.nav.classList.remove('scroll-up');
      }
    }

    this.lastScroll = currentScroll; // Update last scroll position
  }
}

// Register the web component
if (!customElements.get('collection-grid')) {
  customElements.define('collection-grid', CollectionGrid);
}


